import type { FormField, FormInputSchemaParams } from "@/lib/types/form/form-field";
import { resolveFormFieldPublicId } from "@/lib/forms/static-field-id";
import { parseDateDmY } from "@/lib/utils/date-format";

function getDefaultValue(field: FormField): unknown {
	const content = field.content as FormInputSchemaParams & { singleDefaultChecked?: boolean | null };
	const options = ("options" in field ? (field.options ?? []) : []) as { label: string; value: string; defaultChecked?: boolean | null }[];
	const checkedOptions = options.filter((option) => option.defaultChecked);

	if (field.blockType === "form-date") {
		const date = typeof content.default === "string" ? content.default.trim() : "";
		return parseDateDmY(date) ? date : "";
	}

	if (field.blockType === "form-toggle-group" || field.blockType === "form-radio") {
		return checkedOptions[0]?.value ?? "";
	}

	if (field.blockType === "form-checkbox") {
		if (content.type === "single") return content.singleDefaultChecked ?? false;
		return checkedOptions.map((option) => option.value);
	}

	if (field.blockType === "form-select") {
		const selected = checkedOptions.map((option) => ({ label: option.label, value: option.value }));
		return field.settings?.multiple === true ? selected : (selected[0] ?? null);
	}

	if (field.blockType === "form-range-slider") {
		if (content.mode === "range") return [content.defaultMin ?? content.min ?? 0, content.defaultMax ?? content.max ?? 100];
		return content.default ?? content.min ?? 0;
	}

	if (field.blockType === "form-file") return [];
	if (field.blockType === "form-search-profile") return { propertyType: "", rules: {} };

	return content.default ?? "";
}

/**
 * Builds the initial form values, keyed by each field's public id.
 *
 * @param fields - The form fields to build default values for.
 * @returns A map of public field ids to their default values.
 */
export function buildFormDefaultValues(fields?: FormField[] | null): Record<string, unknown> {
	const values: Record<string, unknown> = {};

	for (const field of fields ?? []) {
		if (!field.id || field.blockType === "form-content") continue;
		values[resolveFormFieldPublicId(field)] = getDefaultValue(field);
	}

	return values;
}
